/**
 * The transfer wire shapes — `Transfer`, `TransferCreate`, `TransferUpdate` from
 * `docs/api/paths/transfers.yaml` — and the row type the table and the form render them as.
 * Pure: safe to import from client components.
 */

import type { ApiAccount } from '@/lib/api/ledger/types'
import { EntryStatusType, EntryStatusTypeValue, Page, strEnum } from '@/lib/api/types'
import { TableFilters, urlEnum, urlIds } from '@/lib/table-state'

/** The booking types of a transfer's legs; the contract names them only inside `BookingType`. */
export const TransferType = strEnum(['transfer_cashout', 'transfer_account_opening', 'transfer_deposit', 'transfer'])
export type TransferTypeValue = keyof typeof TransferType

export type ApiTransferWritable = {
  name: string
  /** `Money`: a fixed two-decimal string. */
  amount: string
  /** Account identifiers; a member's `from` must be their own account. */
  from: number
  to: number
  /** ISO date-time. */
  issued: string
}

export type ApiTransfer = ApiTransferWritable & {
  id: number
  status: EntryStatusTypeValue
  createdBy: number
  created: string
  updated: string
}

/** `POST /transfers` — a draft; `book` writes the legs. */
export type TransferCreate = ApiTransferWritable

/** `PATCH /transfers/{id}` — every field optional. */
export type TransferUpdate = Partial<ApiTransferWritable>

/** A transfer as the table and the form render it: dates parsed, both accounts resolved. */
export type TransferRecord = Omit<ApiTransfer, 'issued' | 'created' | 'updated'> & {
  issued: Date
  created: Date
  updated: Date
}

export type TransferRow = {
  transfer: TransferRecord
  from?: ApiAccount
  to?: ApiAccount
}

export const toTransferRow = (transfer: ApiTransfer, accounts: Map<number, ApiAccount>): TransferRow => ({
  transfer: {
    ...transfer,
    issued: new Date(transfer.issued),
    created: new Date(transfer.created),
    updated: new Date(transfer.updated),
  },
  from: accounts.get(transfer.from),
  to: accounts.get(transfer.to),
})

/** `TransferPage`: one page of transfers, with the sum of `amount` over every filtered row (#845). */
export type TransferPage = Page<ApiTransfer> & { totals: { amount: string } }

/** The filters of `GET /transfers`. */
export type TransfersQuery = {
  year?: number
  status?: EntryStatusTypeValue[]
  fromAccounts?: number[]
  toAccounts?: number[]
  q?: string
}

/** `TransfersQuery` as the contract's query params (design.md §8.2). */
export const transfersParams = (q: TransfersQuery) => ({
  year: q.year,
  status: q.status,
  from: q.fromAccounts,
  to: q.toAccounts,
  q: q.q,
})

/**
 * The sort keys of `GET /transfers` that the transfers table offers, a subset of
 * `TransferRepository.sortable` (`backend/app/repositories/transfer.py`). The from- and
 * to-account do not sort (#845): they would order by account id, not by name.
 */
export const TRANSFER_SORTABLE = ['issued', 'created', 'name', 'status', 'amount'] as const

/** `GET /transfers`'s order when the URL names none (`TransferRepository.default_sort`), as table sort state. */
export const TRANSFER_DEFAULT_SORTING = [{ id: 'transfer_issued', desc: true }]

/**
 * The transfers table's URL filters (`f.transfer_*`) as an API query, key by key: a key not listed
 * here never reaches the API, and a value the API would refuse is dropped.
 */
export const transfersQueryOf = (filters: TableFilters): TransfersQuery => ({
  status: urlEnum(EntryStatusType, filters.transfer_status),
  fromAccounts: urlIds(filters.transfer_from),
  toAccounts: urlIds(filters.transfer_to),
})
